import React, { useState, useEffect } from 'react';
import { Transaction, CreateTransaction, UpdateTransaction, Customer, Project, LookupItem } from '../types';
import AutocompleteInput from './AutocompleteInput';
import DateInput from './DateInput';

interface TransactionFormProps {
  transaction?: Transaction;
  customers: Customer[];
  onSubmit: (transaction: CreateTransaction | UpdateTransaction) => void;
  onCancel: () => void;
  onDelete?: (id: number) => void;
  onCopy?: (transaction: Transaction) => void;
  isLoading?: boolean;
  preselectedProjectId?: number;
  preselectedCustomerId?: number;
  mode?: 'create' | 'edit' | 'copy';
  directionOptions: LookupItem[];
  typeOptions: LookupItem[];
  categoryOptions: LookupItem[];
}

const getDefaultTransactionFormData = (): CreateTransaction => ({
  title: '',
  description: '',
  date: new Date().toISOString().split('T')[0],
  projectId: 0,
  amount: 0,
  purchaseIndex: 0,
  exchangeRate: 1,
  currencyId: 2, // Default to USD
  typeId: 1, // Default to Income
  isCompleted: false,
  directionId: 0, // Default to Alex
  category: undefined,
  sourceId: undefined,
  isProcessed: false,
});

const formatTransactionAmountForEdit = (amount: number): string => {
  if (!Number.isFinite(amount)) {
    return String(amount);
  }

  return amount.toFixed(2);
};

const roundTransactionAmountForEdit = (amount: number): number => {
  if (!Number.isFinite(amount)) {
    return amount;
  }

  return Number(formatTransactionAmountForEdit(amount));
};

const getTransactionFormData = (
  transaction: Transaction,
  mode: TransactionFormProps['mode']
): CreateTransaction | UpdateTransaction => ({
  title: transaction.title,
  description: transaction.description,
  date: transaction.date.split('T')[0],
  projectId: transaction.projectId,
  amount: roundTransactionAmountForEdit(transaction.amount),
  purchaseIndex: transaction.purchaseIndex,
  exchangeRate: transaction.exchangeRate,
  currencyId: transaction.currencyId,
  typeId: transaction.typeId,
  isCompleted: transaction.isCompleted,
  directionId: transaction.directionId,
  category: transaction.category,
  sourceId: transaction.sourceId,
  isProcessed: transaction.isProcessed,
  ...(mode === 'edit' ? { id: transaction.id } : {}),
});

const findCustomerByProjectId = (customers: Customer[], projectId?: number): Customer | null => {
  if (!projectId) {
    return null;
  }

  return customers.find(customer => customer.projects.some(project => project.id === projectId)) ?? null;
};

interface ParsedNumberInput {
  normalized: string;
  value: number;
}

const normalizeNumberInput = (rawValue: string): ParsedNumberInput | null => {
  const compact = rawValue
    .trim()
    .replace(/[\s'_’]/g, '')
    .replace(/[^\d,.-]/g, '');
  const sign = compact.startsWith('-') ? '-' : '';
  const unsigned = compact.replace(/-/g, '');

  if (!/\d/.test(unsigned)) {
    return null;
  }

  const commaCount = (unsigned.match(/,/g) || []).length;
  const dotCount = (unsigned.match(/\./g) || []).length;
  const lastCommaIndex = unsigned.lastIndexOf(',');
  const lastDotIndex = unsigned.lastIndexOf('.');
  const onlySeparator = commaCount > 0 && dotCount === 0
    ? ','
    : dotCount > 0 && commaCount === 0
      ? '.'
      : null;

  if (onlySeparator) {
    const groups = unsigned.split(onlySeparator);
    const allGroupedThousands = groups.length > 1
      && groups[0].length >= 1
      && groups[0].length <= 3
      && groups.slice(1).every(group => group.length === 3);

    if (allGroupedThousands) {
      const normalizedThousands = `${sign}${groups.join('')}`;
      return { normalized: normalizedThousands, value: Number(normalizedThousands) };
    }
  }

  const decimalSeparator = lastCommaIndex > lastDotIndex ? ',' : lastDotIndex > -1 ? '.' : null;
  if (!decimalSeparator) {
    const normalizedInteger = `${sign}${unsigned.replace(/[,.]/g, '')}`;
    return { normalized: normalizedInteger, value: Number(normalizedInteger) };
  }

  const separatorIndex = unsigned.lastIndexOf(decimalSeparator);
  const integerPart = unsigned.slice(0, separatorIndex).replace(/[,.]/g, '') || '0';
  const fractionalPart = unsigned.slice(separatorIndex + 1).replace(/[,.]/g, '');
  const normalizedDecimal = fractionalPart
    ? `${sign}${integerPart}.${fractionalPart}`
    : `${sign}${integerPart}`;

  return { normalized: normalizedDecimal, value: Number(normalizedDecimal) };
};

const formatParsedNumberInput = (parsed: ParsedNumberInput): string => {
  const fractionLength = parsed.normalized.includes('.')
    ? parsed.normalized.split('.')[1].length
    : 0;

  if (fractionLength > 0) {
    return parsed.value.toFixed(fractionLength);
  }

  return String(parsed.value);
};

const TransactionFormLoader: React.FC = () => (
  <div className="fade-in">
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      flexDirection: 'column',
      gap: '1rem'
    }}>
      <div className="spinner-border" role="status"></div>
      <p style={{ color: 'var(--text-muted)' }}>Loading transaction...</p>
    </div>
  </div>
);

const TransactionForm: React.FC<TransactionFormProps> = ({
  transaction,
  customers,
  onSubmit,
  onCancel,
  onDelete,
  onCopy,
  isLoading = false,
  preselectedProjectId,
  preselectedCustomerId,
  mode = 'create',
  directionOptions,
  typeOptions,
  categoryOptions,
}) => {
  const initialCustomer = transaction
    ? findCustomerByProjectId(customers, transaction.projectId)
    : preselectedProjectId
      ? findCustomerByProjectId(customers, preselectedProjectId)
      : customers.find(customer => customer.id === preselectedCustomerId) ?? null;

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(initialCustomer);
  const [availableProjects, setAvailableProjects] = useState<Project[]>(initialCustomer?.projects ?? []);
  const [formData, setFormData] = useState<CreateTransaction | UpdateTransaction>(() => (
    transaction ? getTransactionFormData(transaction, mode) : getDefaultTransactionFormData()
  ));
  const [amountRaw, setAmountRaw] = useState<string>(() => (
    transaction ? formatTransactionAmountForEdit(transaction.amount) : '0'
  ));
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (transaction) {
      setFormData(getTransactionFormData(transaction, mode));
      setAmountRaw(formatTransactionAmountForEdit(transaction.amount));

      // Find the customer for this transaction
      const customer = findCustomerByProjectId(customers, transaction.projectId);
      if (customer) {
        setSelectedCustomer(customer);
        setAvailableProjects(customer.projects);
      }
    } else {
      // Handle preselected values
      if (preselectedProjectId && preselectedCustomerId) {
        const customer = customers.find(c => c.id === preselectedCustomerId);
        if (customer) {
          setSelectedCustomer(customer);
          setAvailableProjects(customer.projects);
          setFormData(prev => ({ ...prev, projectId: preselectedProjectId }));
        }
      } else if (preselectedCustomerId) {
        const customer = customers.find(c => c.id === preselectedCustomerId);
        if (customer) {
          setSelectedCustomer(customer);
          setAvailableProjects(customer.projects);
        }
      }
    }
    requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    });
  }, [transaction, customers, preselectedProjectId, preselectedCustomerId, mode]);



  const handleCustomerChange = (customerId: number) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer || null);
    setAvailableProjects(customer?.projects || []);
    setFormData(prev => ({ ...prev, projectId: 0 }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'projectId' || name === 'currencyId' || name === 'typeId' || name === 'directionId' || name === 'category' || name === 'sourceId') {
      processedValue = value === '' ? undefined : parseInt(value);
    } else if (name === 'amount' || name === 'purchaseIndex' || name === 'exchangeRate') {
      const parsed = normalizeNumberInput(value);
      processedValue = value.trim() === '' ? '' : parsed?.value ?? Number.NaN;
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.projectId || formData.projectId < 1) {
      newErrors.projectId = 'Project is required';
    }

    if (formData.amount === undefined || formData.amount === null || Number.isNaN(formData.amount)) {
      newErrors.amount = 'Amount is required';
    }

    if (!formData.currencyId || formData.currencyId < 1) {
      newErrors.currencyId = 'Currency is required';
    }

    if (!formData.typeId || formData.typeId < 1) {
      newErrors.typeId = 'Type is required';
    }

    if (formData.directionId === undefined || formData.directionId === null) {
      newErrors.directionId = 'Direction is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const currencyOptions = [
    { value: 1, label: 'UAH' },
    { value: 2, label: 'USD' },
    { value: 3, label: 'RUB' },
    { value: 4, label: 'EUR' },
    { value: 5, label: 'SEK' },
  ];

  const formattedDirectionOptions = directionOptions.map(option => ({
    value: option.id,
    label: option.title,
  }));

  const formattedTypeOptions = typeOptions.map(option => ({
    value: option.id,
    label: option.title,
  }));

  const formattedCategoryOptions = categoryOptions.map(option => ({
    value: option.id,
    label: option.title,
  }));

  const typeSelectValue = formattedTypeOptions.length > 0 ? formData.typeId ?? '' : '';
  const categorySelectValue = formattedCategoryOptions.length > 0 ? (formData.category ?? '') : '';

  const updateAmount = (raw: string) => {
    setAmountRaw(raw);
    const parsed = normalizeNumberInput(raw);
    setFormData(prev => ({
      ...prev,
      amount: raw.trim() === '' ? 0 : parsed?.value ?? Number.NaN,
    }));
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const normalizeAmountDisplay = () => {
    const parsed = normalizeNumberInput(amountRaw);
    if (parsed) {
      setAmountRaw(formatParsedNumberInput(parsed));
    }
  };

  const handleAmountPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const input = e.currentTarget;
    const selectionStart = input.selectionStart ?? amountRaw.length;
    const selectionEnd = input.selectionEnd ?? amountRaw.length;
    const nextRaw = `${amountRaw.slice(0, selectionStart)}${pastedText}${amountRaw.slice(selectionEnd)}`;
    const parsed = normalizeNumberInput(nextRaw);

    if (parsed) {
      updateAmount(formatParsedNumberInput(parsed));
      return;
    }

    updateAmount(nextRaw);
  };

  if ((mode === 'edit' || mode === 'copy') && !transaction) {
    return <TransactionFormLoader />;
  }

  return (
    <div className="card transaction-form-card">
      <div className="card-header">
        <h5 className="mb-0">
          {mode === 'edit' ? `Edit Transaction: ${transaction?.title}` : 
           mode === 'copy' ? `Copy Transaction: ${transaction?.title}` : 
           'Create New Transaction'}
        </h5>
        {transaction && (
          <div className="text-muted small">
            Created: {new Date(transaction.createdDate || '').toLocaleString()}
            {transaction.updatedDate && (
              <span> | Updated: {new Date(transaction.updatedDate).toLocaleString()}</span>
            )}
          </div>
        )}
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit} className="transaction-form">
          <div className="row">
            <div className="col-md-12 mb-3">
              <AutocompleteInput
                id="customer"
                label="Customer"
                placeholder="Type customer name..."
                items={customers}
                value={selectedCustomer?.id}
                onChange={(value) => handleCustomerChange(Number(value))}
                getItemValue={(customer) => customer.id}
                getItemLabel={(customer) => customer.title}
                disabled={isLoading}
                required
                noItemsMessage="No customers found"
                minCharsToShow={1}
              />
            </div>

            {selectedCustomer && (
              <div className="col-md-12 mb-3">
                <AutocompleteInput
                  id="projectId"
                  label="Project"
                  placeholder="Type project name..."
                  items={availableProjects}
                  value={formData.projectId}
                  onChange={(value) => setFormData(prev => ({ ...prev, projectId: Number(value) }))}
                  getItemValue={(project) => project.id}
                  getItemLabel={(project) => project.title}
                  disabled={isLoading}
                  required
                  error={errors.projectId}
                  noItemsMessage="No projects found"
                  minCharsToShow={1}
                />
              </div>
            )}
          </div>

          <div className="row">
            <div className="col-md-12 mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.title && <div className="invalid-feedback">{errors.title}</div>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="date" className="form-label">
                Date <span className="text-danger">*</span>
              </label>
              <DateInput
                className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                id="date"
                name="date"
                value={formData.date}
                onChange={(value) => handleInputChange({ target: { name: 'date', value } } as any)}
                disabled={isLoading}
                required
              />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="category" className="form-label">Category</label>
              <select
                className="form-select"
                id="category"
                name="category"
                value={categorySelectValue}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Select category...</option>
                {formattedCategoryOptions.length > 0 ? (
                  formattedCategoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    Loading categories...
                  </option>
                )}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="description" className="form-label">Description</label>
            <textarea
              className="form-control"
              id="description"
              name="description"
              rows={3}
              value={formData.description || ''}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="sourceId" className="form-label">Source</label>
              <div className="d-flex">
                <select
                  className="form-select"
                  id="sourceId"
                  name="sourceId"
                  value={formData.sourceId || ''}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  <option value="">No source...</option>
                  {formattedDirectionOptions.length > 0 ? (
                    formattedDirectionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Loading directions...
                    </option>
                  )}
                </select>
                <button
                  type="button"
                  className="btn btn-outline-secondary ms-2"
                  onClick={() => setFormData(prev => ({ ...prev, sourceId: undefined }))}
                  disabled={isLoading}
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="directionId" className="form-label">
                Direction <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.directionId ? 'is-invalid' : ''}`}
                id="directionId"
                name="directionId"
                value={formData.directionId ?? ''}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Select direction...</option>
                {formattedDirectionOptions.length > 0 ? (
                  formattedDirectionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    Loading directions...
                  </option>
                )}
              </select>
              {errors.directionId && <div className="invalid-feedback">{errors.directionId}</div>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="amount" className="form-label">
                Amount <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                id="amount"
                name="amount"
                value={amountRaw}
                onChange={(e) => updateAmount(e.target.value)}
                onPaste={handleAmountPaste}
                onBlur={normalizeAmountDisplay}
                onFocus={(e) => e.target.select()}
                disabled={isLoading}
              />
              {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label htmlFor="typeId" className="form-label">
                Type <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.typeId ? 'is-invalid' : ''}`}
                id="typeId"
                name="typeId"
                value={typeSelectValue}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Select type...</option>
                {formattedTypeOptions.length > 0 ? (
                  formattedTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    Loading types...
                  </option>
                )}
              </select>
              {errors.typeId && <div className="invalid-feedback">{errors.typeId}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label htmlFor="currencyId" className="form-label">
                Currency <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.currencyId ? 'is-invalid' : ''}`}
                id="currencyId"
                name="currencyId"
                value={formData.currencyId}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Select currency...</option>
                {currencyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.currencyId && <div className="invalid-feedback">{errors.currencyId}</div>}
            </div>
          </div>

          <div className="row">
            <div className="col-12 mb-3">
              <div className="d-flex gap-4 flex-wrap">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isCompleted"
                    name="isCompleted"
                    checked={formData.isCompleted}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <label className="form-check-label" htmlFor="isCompleted">
                    Is Completed
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isProcessed"
                    name="isProcessed"
                    checked={formData.isProcessed}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <label className="form-check-label" htmlFor="isProcessed">
                    Is Processed
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 form-actions">
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              {mode !== 'create' && onDelete && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => onDelete(transaction!.id)}
                  disabled={isLoading}
                >
                  Delete
                </button>
              )}
            </div>
            {mode === 'edit' && transaction && onCopy && (
              <div className="d-flex">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => onCopy(transaction)}
                  disabled={isLoading}
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionForm;
